/**
 * LOCATION: dashboard-business/src/pages/Agents.js
 * Basic plan: clicking "Add Agent" shows upgrade modal instead
 * FA icons only — zero emojis, zero lucide
 */

import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import API from '../services/api';
import AuthService from '../services/auth';

const loadFA = () => {
  if (!document.getElementById('fa-cdn-agents')) {
    const fa = document.createElement('link');
    fa.id = 'fa-cdn-agents'; fa.rel = 'stylesheet';
    fa.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css';
    document.head.appendChild(fa);
  }
};

const Agents = () => {
  const [agents,     setAgents]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [showModal,  setShowModal]  = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState('');
  const [copied,     setCopied]     = useState(false);
  const [newAgent,   setNewAgent]   = useState({ name: '', email: '', role: 'agent' });
  const user = AuthService.getCurrentUser();
  const plan = user?.plan || 'basic';
  const isBasic = plan === 'basic' || plan === 'free';

  useEffect(() => { loadFA(); loadAgents(); }, []);

  const loadAgents = async () => {
    setLoading(true);
    try {
      const response = await API.getAgents(user?.id);
      if (response.success) setAgents(response.agents || []);
    } catch (err) { console.error('Failed to load agents:', err); }
    finally { setLoading(false); }
  };

  // Gate: Basic plan cannot add agents
  const handleAddAgentClick = () => {
    if (isBasic) { setShowUpgrade(true); return; }
    setShowModal(true);
  };

  const handleAddAgent = async (e) => {
    e.preventDefault(); setError('');
    if (!newAgent.name.trim())  { setError('Agent name is required'); return; }
    if (!newAgent.email.trim()) { setError('Agent email is required'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newAgent.email)) { setError('Please enter a valid email'); return; }
    setSaving(true);
    try {
      const response = await API.addAgent({
        name: newAgent.name.trim(), email: newAgent.email.trim(),
        role: newAgent.role, businessId: user?.id,
      });
      if (response.success) {
        setAgents(prev => [...prev, response.agent]);
        setNewAgent({ name: '', email: '', role: 'agent' });
        setShowModal(false);
      } else { setError(response.message || 'Failed to add agent'); }
    } catch { setError('Failed to add agent. Please try again.'); }
    finally { setSaving(false); }
  };

  const handleDeleteAgent = async (agentId, agentName) => {
    if (!window.confirm(`Remove ${agentName} from your team?`)) return;
    try {
      const response = await API.deleteAgent(agentId);
      if (response.success) setAgents(prev => prev.filter(a => a.id !== agentId));
    } catch (err) { console.error('Failed to delete agent:', err); }
  };

  const copyBusinessId = () => {
    navigator.clipboard.writeText(user?.apiKey || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  };

  const getInitials = (name) =>
    name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        .ag-layout{display:flex;min-height:100vh;font-family:'DM Sans',sans-serif;}
        .ag-main{flex:1;padding:32px;background:#f8fafc;overflow-y:auto;}
        .ag-header{display:flex;justify-content:space-between;align-items:center;margin-bottom:24px;flex-wrap:wrap;gap:12px;}
        .ag-title{font-size:28px;font-weight:800;color:#111827;margin:0 0 4px;display:flex;align-items:center;gap:10px;}
        .ag-sub{font-size:14px;color:#6b7280;margin:0;}
        .ag-add-btn{display:flex;align-items:center;gap:7px;padding:10px 20px;background:linear-gradient(135deg,#667eea,#764ba2);color:#fff;border:none;border-radius:10px;cursor:pointer;font-weight:700;font-size:14px;font-family:'DM Sans',sans-serif;transition:all 0.2s;box-shadow:0 4px 12px rgba(102,126,234,0.3);}
        .ag-add-btn:hover{transform:translateY(-1px);box-shadow:0 6px 16px rgba(102,126,234,0.4);}
        .ag-share-card{background:#fff;border:1px solid #e5e7eb;border-radius:14px;padding:20px 24px;margin-bottom:24px;display:flex;justify-content:space-between;align-items:center;gap:20px;flex-wrap:wrap;box-shadow:0 1px 4px rgba(0,0,0,0.04);}
        .ag-share-title{margin:0 0 6px;font-size:15px;font-weight:700;color:#111827;}
        .ag-share-txt{margin:0;font-size:13px;color:#6b7280;line-height:1.6;}
        .ag-id-code{font-family:monospace;font-size:13px;background:#f3f4f6;padding:8px 12px;border-radius:8px;color:#374151;}
        .ag-copy-btn{display:flex;align-items:center;gap:6px;padding:8px 14px;color:#fff;border:none;border-radius:8px;cursor:pointer;font-size:13px;font-weight:700;font-family:'DM Sans',sans-serif;transition:background 0.2s;}
        .ag-list{display:flex;flex-direction:column;gap:10px;}
        .ag-card{background:#fff;padding:16px 20px;border-radius:12px;border:1px solid #e5e7eb;display:flex;justify-content:space-between;align-items:center;box-shadow:0 1px 3px rgba(0,0,0,0.04);flex-wrap:wrap;gap:10px;}
        .ag-left{display:flex;align-items:center;gap:14px;flex-wrap:wrap;}
        .ag-avatar{width:44px;height:44px;background:linear-gradient(135deg,#667eea,#764ba2);border-radius:50%;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:15px;flex-shrink:0;}
        .ag-name{font-size:15px;font-weight:700;color:#111827;margin:0 0 3px;}
        .ag-email{font-size:12px;color:#6b7280;margin:0;display:flex;align-items:center;gap:4px;}
        .ag-role{padding:4px 10px;border-radius:8px;font-size:12px;font-weight:600;display:flex;align-items:center;gap:5px;}
        .ag-right{display:flex;align-items:center;gap:12px;}
        .ag-status{display:flex;align-items:center;gap:6px;padding:4px 10px;border-radius:20px;font-size:12px;font-weight:600;}
        .ag-del{background:transparent;border:none;color:#ef4444;cursor:pointer;padding:6px;border-radius:6px;display:flex;align-items:center;transition:background 0.15s;}
        .ag-del:hover{background:#fff1f2;}
        .ag-loading{text-align:center;padding:60px 20px;color:#6b7280;}
        .ag-empty{text-align:center;padding:60px 20px;color:#6b7280;background:#fff;border-radius:14px;border:1px solid #e5e7eb;}
        .ag-empty-btn{display:inline-flex;align-items:center;gap:7px;padding:10px 20px;background:#667eea;color:#fff;border:none;border-radius:8px;cursor:pointer;font-weight:700;font-size:14px;font-family:'DM Sans',sans-serif;margin-top:16px;}
        /* Modal overlay */
        .ag-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.5);display:flex;align-items:center;justify-content:center;z-index:1000;padding:16px;}
        .ag-modal{background:#fff;border-radius:18px;width:100%;max-width:460px;box-shadow:0 24px 60px rgba(0,0,0,0.28);animation:ag-slide-up 0.25s ease;}
        @keyframes ag-slide-up{from{opacity:0;transform:translateY(20px);}to{opacity:1;transform:translateY(0);}}
        .ag-modal-hdr{padding:20px 24px;border-bottom:1px solid #e5e7eb;display:flex;justify-content:space-between;align-items:center;}
        .ag-modal-title{font-size:18px;font-weight:800;color:#111827;margin:0;}
        .ag-close{background:#f3f4f6;border:none;border-radius:8px;width:32px;height:32px;cursor:pointer;display:flex;align-items:center;justify-content:center;color:#6b7280;font-size:15px;}
        .ag-modal-err{background:#fee2e2;border:1px solid #fca5a5;color:#b91c1c;padding:10px 24px;font-size:13px;display:flex;align-items:center;gap:7px;}
        .ag-lbl{display:block;font-size:13px;font-weight:700;color:#374151;margin-bottom:7px;margin-top:16px;}
        .ag-inp{width:100%;padding:11px 14px;border:2px solid #e5e7eb;border-radius:10px;font-size:14px;outline:none;box-sizing:border-box;font-family:'DM Sans',sans-serif;transition:border-color 0.2s;}
        .ag-inp:focus{border-color:#667eea;box-shadow:0 0 0 3px rgba(102,126,234,0.1);}
        .ag-note{background:#fffbeb;border:1px solid #fde68a;border-radius:10px;padding:12px 14px;margin:16px 0;display:flex;align-items:flex-start;gap:8px;font-size:13px;color:#92400e;line-height:1.5;}
        .ag-modal-footer{display:flex;gap:10px;justify-content:flex-end;padding:0 24px 24px;}
        .ag-cancel-btn{padding:10px 18px;background:#f3f4f6;border:none;border-radius:10px;cursor:pointer;font-weight:600;color:#374151;font-size:14px;font-family:'DM Sans',sans-serif;}
        .ag-save-btn{padding:10px 22px;background:linear-gradient(135deg,#667eea,#764ba2);color:#fff;border:none;border-radius:10px;cursor:pointer;font-weight:700;font-size:14px;font-family:'DM Sans',sans-serif;display:flex;align-items:center;gap:6px;}
        /* Upgrade modal */
        .ag-upgrade-modal{background:#fff;border-radius:20px;width:100%;max-width:420px;box-shadow:0 24px 60px rgba(0,0,0,0.28);overflow:hidden;animation:ag-slide-up 0.25s ease;}
        .ag-upgrade-top{background:linear-gradient(135deg,#667eea,#764ba2);padding:32px 28px;text-align:center;}
        .ag-upgrade-icon{width:64px;height:64px;background:rgba(255,255,255,0.2);border-radius:18px;display:flex;align-items:center;justify-content:center;margin:0 auto 16px;}
        .ag-upgrade-title{font-size:22px;font-weight:800;color:#fff;margin:0 0 8px;}
        .ag-upgrade-sub{font-size:14px;color:rgba(255,255,255,0.8);margin:0;line-height:1.6;}
        .ag-upgrade-body{padding:24px 28px;}
        .ag-upgrade-feat{display:flex;align-items:center;gap:10px;padding:9px 0;border-bottom:1px solid #f3f4f6;font-size:14px;color:#374151;}
        .ag-upgrade-feat:last-child{border-bottom:none;}
        .ag-upgrade-actions{display:flex;gap:10px;margin-top:20px;}
        .ag-upgrade-cta{flex:1;padding:12px;background:linear-gradient(135deg,#667eea,#764ba2);color:#fff;border:none;border-radius:11px;cursor:pointer;font-weight:700;font-size:14px;font-family:'DM Sans',sans-serif;display:flex;align-items:center;justify-content:center;gap:7px;}
        .ag-upgrade-dismiss{padding:12px 16px;background:#f3f4f6;border:none;border-radius:11px;cursor:pointer;font-weight:600;font-size:14px;font-family:'DM Sans',sans-serif;color:#374151;}
        @media(max-width:768px){.ag-main{padding:20px 16px;}.ag-share-card{flex-direction:column;align-items:flex-start;}.ag-right{gap:8px;}.ag-title{font-size:22px;}}
      `}</style>

      <div className="ag-layout">
        <Sidebar />
        <main className="ag-main">

          {/* Header */}
          <div className="ag-header">
            <div>
              <h1 className="ag-title">
                <i className="fa-solid fa-users" style={{ color: '#667eea' }}></i>
                Agents
              </h1>
              <p className="ag-sub">Manage your support team</p>
            </div>
            <button className="ag-add-btn" onClick={handleAddAgentClick}>
              <i className="fa-solid fa-user-plus"></i>
              Add Agent
              {isBasic && <i className="fa-solid fa-lock" style={{ fontSize: 11, opacity: 0.8 }}></i>}
            </button>
          </div>

          {/* Business ID sharing */}
          {!isBasic && (
            <div className="ag-share-card">
              <div style={{ flex: 1 }}>
                <h4 className="ag-share-title">
                  <i className="fa-solid fa-share-nodes" style={{ marginRight: 8, color: '#667eea' }}></i>
                  Agent Login Info
                </h4>
                <p className="ag-share-txt">
                  Share this Business ID with agents to log into the Agent Dashboard.<br />
                  Agent Dashboard URL: <strong>http://localhost:3002</strong>
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <code className="ag-id-code">{user?.apiKey || 'Loading...'}</code>
                <button
                  className="ag-copy-btn"
                  onClick={copyBusinessId}
                  style={{ background: copied ? '#10b981' : '#667eea' }}
                >
                  <i className={`fa-solid ${copied ? 'fa-check' : 'fa-copy'}`}></i>
                  {copied ? 'Copied!' : 'Copy ID'}
                </button>
              </div>
            </div>
          )}

          {/* Basic plan info banner */}
          {isBasic && (
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, padding: '14px 18px', marginBottom: 24, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <i className="fa-solid fa-lock" style={{ color: '#f59e0b', fontSize: 16, marginTop: 1 }}></i>
              <div>
                <p style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 700, color: '#92400e' }}>
                  Agents are not available on the Basic plan
                </p>
                <p style={{ margin: 0, fontSize: 13, color: '#92400e', lineHeight: 1.6 }}>
                  Upgrade to <strong>Pro</strong> (up to 5 agents) or <strong>PREMIUM</strong> (unlimited agents) to add your support team.
                  <a href="/billing" style={{ marginLeft: 8, color: '#667eea', fontWeight: 700, textDecoration: 'none' }}>
                    View Plans <i className="fa-solid fa-arrow-right" style={{ fontSize: 11 }}></i>
                  </a>
                </p>
              </div>
            </div>
          )}

          {/* Agents list */}
          {loading ? (
            <div className="ag-loading">
              <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: 28, marginBottom: 10, color: '#667eea', display: 'block' }}></i>
              <p>Loading agents...</p>
            </div>
          ) : agents.length === 0 ? (
            <div className="ag-empty">
              <i className="fa-solid fa-users-slash" style={{ fontSize: 56, opacity: 0.2, display: 'block', marginBottom: 16, color: '#6b7280' }}></i>
              <h3 style={{ fontSize: 20, color: '#111827', margin: '0 0 8px' }}>No agents yet</h3>
              <p style={{ margin: 0, fontSize: 14, color: '#6b7280' }}>
                {isBasic
                  ? 'Upgrade to Pro or PREMIUM to add agents to your team.'
                  : 'Add your first agent to start handling customer chats side-by-side with AI.'}
              </p>
              <button className="ag-empty-btn" onClick={handleAddAgentClick}>
                <i className="fa-solid fa-user-plus"></i>
                {isBasic ? 'Upgrade to Add Agents' : 'Add Your First Agent'}
              </button>
            </div>
          ) : (
            <div className="ag-list">
              {agents.map(agent => (
                <div key={agent.id} className="ag-card">
                  <div className="ag-left">
                    <div className="ag-avatar">{getInitials(agent.name)}</div>
                    <div>
                      <p className="ag-name">{agent.name}</p>
                      <p className="ag-email">
                        <i className="fa-solid fa-envelope" style={{ fontSize: 11, color: '#9ca3af' }}></i>
                        {agent.email}
                      </p>
                    </div>
                    <span className="ag-role" style={{ background: agent.role === 'admin' ? '#eff6ff' : '#f3f4f6', color: agent.role === 'admin' ? '#1e40af' : '#374151' }}>
                      <i className={`fa-solid ${agent.role === 'admin' ? 'fa-shield-halved' : 'fa-headset'}`} style={{ fontSize: 11 }}></i>
                      {agent.role || 'agent'}
                    </span>
                  </div>
                  <div className="ag-right">
                    <span className="ag-status" style={{ background: agent.status === 'online' ? '#dcfce7' : '#f3f4f6', color: agent.status === 'online' ? '#166534' : '#6b7280' }}>
                      <i className={`fa-solid ${agent.status === 'online' ? 'fa-circle-check' : 'fa-circle'}`} style={{ fontSize: 10 }}></i>
                      {agent.status || 'offline'}
                    </span>
                    <button className="ag-del" onClick={() => handleDeleteAgent(agent.id, agent.name)} title="Remove agent">
                      <i className="fa-solid fa-trash-can" style={{ fontSize: 15 }}></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── ADD AGENT MODAL ── */}
          {showModal && (
            <div className="ag-overlay" onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}>
              <div className="ag-modal">
                <div className="ag-modal-hdr">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: '#eff0ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <i className="fa-solid fa-user-plus" style={{ color: '#667eea', fontSize: 15 }}></i>
                    </div>
                    <h3 className="ag-modal-title">Add New Agent</h3>
                  </div>
                  <button className="ag-close" onClick={() => setShowModal(false)}>
                    <i className="fa-solid fa-xmark"></i>
                  </button>
                </div>

                {error && (
                  <div className="ag-modal-err">
                    <i className="fa-solid fa-triangle-exclamation"></i>{error}
                  </div>
                )}

                <form onSubmit={handleAddAgent} style={{ padding: '0 24px 0' }}>
                  <div>
                    <label className="ag-lbl">
                      <i className="fa-solid fa-user" style={{ marginRight: 6, color: '#9ca3af', fontSize: 12 }}></i>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={newAgent.name}
                      onChange={e => { setNewAgent({ ...newAgent, name: e.target.value }); setError(''); }}
                      placeholder="e.g., John Doe"
                      className="ag-inp"
                      autoFocus
                    />
                  </div>

                  <div>
                    <label className="ag-lbl">
                      <i className="fa-solid fa-envelope" style={{ marginRight: 6, color: '#9ca3af', fontSize: 12 }}></i>
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={newAgent.email}
                      onChange={e => { setNewAgent({ ...newAgent, email: e.target.value }); setError(''); }}
                      placeholder="agent@yourcompany.com"
                      className="ag-inp"
                    />
                  </div>

                  <div>
                    <label className="ag-lbl">
                      <i className="fa-solid fa-tag" style={{ marginRight: 6, color: '#9ca3af', fontSize: 12 }}></i>
                      Role
                    </label>
                    <select
                      value={newAgent.role}
                      onChange={e => setNewAgent({ ...newAgent, role: e.target.value })}
                      className="ag-inp"
                    >
                      <option value="agent">Agent — Can handle chats</option>
                      <option value="admin">Admin — Can handle chats + manage agents</option>
                    </select>
                  </div>

                  <div className="ag-note">
                    <i className="fa-solid fa-circle-info" style={{ color: '#92400e', flexShrink: 0, marginTop: 1 }}></i>
                    <span>The agent logs in using their email and your Business ID. Share the ID shown on this page.</span>
                  </div>

                  <div className="ag-modal-footer" style={{ paddingLeft: 0, paddingRight: 0 }}>
                    <button type="button" className="ag-cancel-btn" onClick={() => setShowModal(false)}>
                      <i className="fa-solid fa-xmark" style={{ marginRight: 6 }}></i>Cancel
                    </button>
                    <button type="submit" className="ag-save-btn" disabled={saving}>
                      {saving
                        ? <><i className="fa-solid fa-spinner fa-spin"></i>Adding...</>
                        : <><i className="fa-solid fa-user-plus"></i>Add Agent</>}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* ── UPGRADE MODAL (Basic plan) ── */}
          {showUpgrade && (
            <div className="ag-overlay" onClick={e => { if (e.target === e.currentTarget) setShowUpgrade(false); }}>
              <div className="ag-upgrade-modal">
                <div className="ag-upgrade-top">
                  <div className="ag-upgrade-icon">
                    <i className="fa-solid fa-users" style={{ color: '#fff', fontSize: 26 }}></i>
                  </div>
                  <h3 className="ag-upgrade-title">Agents Not Available</h3>
                  <p className="ag-upgrade-sub">
                    This feature is not available on the Basic plan.<br />
                    Upgrade to Pro or PREMIUM to add human agents.
                  </p>
                </div>
                <div className="ag-upgrade-body">
                  {[
                    { icon: 'fa-bolt',   color: '#667eea', text: 'Pro — Up to 5 agents, 1,000 msgs/mo' },
                    { icon: 'fa-crown',  color: '#f59e0b', text: 'PREMIUM — Unlimited agents, unlimited msgs' },
                    { icon: 'fa-headset',color: '#10b981', text: 'AI + Human chat handoff' },
                    { icon: 'fa-language', color: '#8b5cf6', text: 'Multilingual AI support' },
                  ].map((f, i) => (
                    <div key={i} className="ag-upgrade-feat">
                      <i className={`fa-solid ${f.icon}`} style={{ color: f.color, fontSize: 14, width: 18, textAlign: 'center' }}></i>
                      <span>{f.text}</span>
                    </div>
                  ))}
                  <div className="ag-upgrade-actions">
                    <button className="ag-upgrade-dismiss" onClick={() => setShowUpgrade(false)}>
                      Not now
                    </button>
                    <a href="/billing" className="ag-upgrade-cta" style={{ textDecoration: 'none' }}>
                      <i className="fa-solid fa-arrow-up"></i>
                      View Plans
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}

        </main>
      </div>
    </>
  );
};

export default Agents;